import {
  Component,
  OnInit,
  OnDestroy,
  ViewEncapsulation,
  HostListener,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { NotificationService } from '../../../../sharedFeatures/services/notification.service';
import { CurrentUserService } from '../../../../sharedFeatures/services/current-user.service';
import { ModuleSelectionService } from '../../services/module-selection.service';
import { LoginService } from '../../services/login.service';
import {
  UserModule,
  SelectModuleRequest,
  UserRole,
} from '../../models/user-module.model';

@Component({
  selector: 'app-module-selection',
  templateUrl: './module-selection.component.html',
  styleUrls: ['./module-selection.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ModuleSelectionComponent implements OnInit, OnDestroy {
  modules: UserModule[] = [];
  subscriptions: Subscription[] = [];
  isLoading: boolean = true;
  pendingReturnUrl: string | null = null;
  currentUserName: string = '';
  showDropdown: boolean = false;

  constructor(
    private moduleSelectionService: ModuleSelectionService,
    private loginService: LoginService,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private currentUserService: CurrentUserService
  ) {}

  ngOnInit(): void {
    // Get current user name with multiple fallbacks
    const currentUser = this.currentUserService.getCurrentUser();
    if (currentUser) {
      this.currentUserName =
        currentUser.name ||
        currentUser.userName ||
        `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() ||
        'User';
    } else {
      this.currentUserName = 'User';
    }

    // Check for pending return URL from both query params and session storage
    this.route.queryParams.subscribe(params => {
      // Priority 1: Query parameter
      this.pendingReturnUrl = params['returnUrl'];

      // Priority 2: Session storage (if not in query params)
      if (!this.pendingReturnUrl) {
        this.pendingReturnUrl = this.currentUserService.getReturnUrl();
      }

      // Clear from session storage immediately after capturing it
      // This prevents it from persisting if user navigates away or refreshes
      if (this.pendingReturnUrl) {
        this.currentUserService.clearReturnUrl();
      }
    });

    // Load user modules
    this.loadUserModules();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  /**
   * Load all modules accessible by the user
   */
  loadUserModules(): void {
    this.isLoading = true;
    const sub = this.moduleSelectionService.getUserModules().subscribe(
      (modules: UserModule[]) => {
        this.modules = modules;

        // Auto-select single role for each module, set undefined for multiple roles
        this.modules.forEach(module => {
          if (module.roles && module.roles.length === 1) {
            module.selectedRole = module.roles[0];
          } else if (module.roles && module.roles.length > 1) {
            // Set to undefined to show "Select a role" as default
            module.selectedRole = undefined;
          }
        });

        this.isLoading = false;

        // Note: Auto-redirect for single module/role is now handled in login component
        // to prevent flash of module selection screen
      },
      (error: any) => {
        // this.notificationService.showErrorTranslated(
        //   'error.shared.operationFailed',
        //   ''
        // );
        this.isLoading = false;
      }
    );
    this.subscriptions.push(sub);
  }

  /**
   * Navigate to selected module
   */
  goToModule(module: UserModule): void {
    // Validate role selection
    if (module.roles.length > 1 && !module.selectedRole) {
      this.notificationService.showwarningTranslated(
        'users.moduleSelection.pleaseSelectRole',
        ''
      );
      return;
    }

    // Check if module link is internal (same origin)
    try {
      const moduleUrl = new URL(module.link);
      const isInternal = moduleUrl.origin === window.location.origin;
      if (isInternal && module.id === 5) {
        // Internal module - Check if we need to re-authenticate
        const currentUser = this.currentUserService.getCurrentUser();
        const storedCredentials = sessionStorage.getItem(
          'tempLoginCredentials'
        );

        // Determine if re-authentication is needed
        const needsReAuth = this.needsReAuthentication(
          currentUser,
          module,
          storedCredentials
        );

        if (needsReAuth && storedCredentials) {
          // Re-authenticate with selected module and role to get updated token
          const credentials = JSON.parse(storedCredentials);

          const sub = this.loginService
            .loginWithModuleAndRole(
              credentials.userName,
              credentials.password,
              module.id,
              module.selectedRole?.id
            )
            .subscribe(
              userDetails => {
                // Update current user with NEW token that includes role information
                this.currentUserService.setCurrentUser(userDetails);

                // Clear stored credentials after successful re-authentication
                sessionStorage.removeItem('tempLoginCredentials');

                // Get redirect URL from service
                const redirectUrl =
                  this.moduleSelectionService.getModuleRedirectUrl(
                    module.id,
                    module.selectedRole?.id
                  );

                // Check if pendingReturnUrl belongs to the selected module
                const shouldUseReturnUrl = this.shouldUseReturnUrl(
                  module.id,
                  this.pendingReturnUrl
                );

                // Navigate to module
                if (this.pendingReturnUrl && shouldUseReturnUrl) {
                  this.router.navigateByUrl(this.pendingReturnUrl);
                } else {
                  if (this.pendingReturnUrl && !shouldUseReturnUrl) {
                    // User selected wrong module for the requested URL
                    this.notificationService.showwarningTranslated(
                      'users.moduleSelection.wrongModuleWarning',
                      ''
                    );
                  }
                  this.router.navigateByUrl(redirectUrl);
                }
              },
              error => {
                // this.notificationService.showErrorTranslated(
                //   'error.shared.operationFailed',
                //   ''
                // );
              }
            );
          this.subscriptions.push(sub);
        } else if (needsReAuth && !storedCredentials) {
          // Credentials not available but re-auth needed - redirect to login
          this.notificationService.showErrorTranslated(
            'users.moduleSelection.sessionExpired',
            ''
          );
          const returnUrl = this.router.url;
          this.router.navigate(['/login'], {
            queryParams: { returnUrl: returnUrl },
          });
          return;
        } else {
          // User already has valid token with role - just update and navigate
          if (currentUser) {
            currentUser.moduleId = module.id;
            if (module.selectedRole?.id !== undefined) {
              currentUser.roleId = module.selectedRole.id;
              currentUser.roleName = module.selectedRole.name;
            }
            this.currentUserService.setCurrentUser(currentUser);
          }

          // Clear stored credentials if they exist
          sessionStorage.removeItem('tempLoginCredentials');

          // Get redirect URL from service
          const redirectUrl = this.moduleSelectionService.getModuleRedirectUrl(
            module.id,
            module.selectedRole?.id
          );

          // Check if pendingReturnUrl belongs to the selected module
          const shouldUseReturnUrl = this.shouldUseReturnUrl(
            module.id,
            this.pendingReturnUrl
          );

          // Navigate to module
          if (this.pendingReturnUrl && shouldUseReturnUrl) {
            this.router.navigateByUrl(this.pendingReturnUrl);
          } else {
            if (this.pendingReturnUrl && !shouldUseReturnUrl) {
              // User selected wrong module for the requested URL
              this.notificationService.showwarningTranslated(
                'users.moduleSelection.wrongModuleWarning',
                ''
              );
            }
            this.router.navigateByUrl(redirectUrl);
          }
        }
      } else {
        // External module OR non-mystery-shopper internal module
        // 1. Generate token
        // 2. Exchange token to get user details with role information
        // 3. Navigate to module
        const sub = this.moduleSelectionService
          .generateToken(module.id, module.selectedRole?.id)
          .subscribe(
            (token: string) => {
              // Exchange token to get authenticated user details
              this.loginService.exchangeToken({ token: token }).subscribe(
                userDetails => {
                  // Update current user with exchanged token details
                  this.currentUserService.setCurrentUser(userDetails);

                  // Clear stored credentials
                  sessionStorage.removeItem('tempLoginCredentials');

                  // Check if this is external module (different origin)
                  if (!isInternal) {
                    // External module - redirect to external URL with token
                    let tokenUrl = `${module.link}?token=${token}`;

                    // Append return URL if exists
                    if (this.pendingReturnUrl) {
                      tokenUrl += `&returnUrl=${encodeURIComponent(this.pendingReturnUrl)}`;
                    }

                    window.location.href = tokenUrl;
                  } else {
                    // Internal module (but not mystery shopper) - navigate using router
                    const redirectUrl =
                      this.moduleSelectionService.getModuleRedirectUrl(
                        module.id,
                        module.selectedRole?.id
                      );

                    // Check if pendingReturnUrl belongs to the selected module
                    const shouldUseReturnUrl = this.shouldUseReturnUrl(
                      module.id,
                      this.pendingReturnUrl
                    );

                    if (this.pendingReturnUrl && shouldUseReturnUrl) {
                      this.router.navigateByUrl(this.pendingReturnUrl);
                    } else {
                      if (this.pendingReturnUrl && !shouldUseReturnUrl) {
                        // User selected wrong module for the requested URL
                        this.notificationService.showwarningTranslated(
                          'users.moduleSelection.wrongModuleWarning',
                          ''
                        );
                      }
                      this.router.navigateByUrl(redirectUrl);
                    }
                  }
                },
                error => {
                  // this.notificationService.showErrorTranslated(
                  //   'error.shared.operationFailed',
                  //   ''
                  // );
                }
              );
            },
            error => {
              // this.notificationService.showErrorTranslated(
              //   'error.shared.operationFailed',
              //   ''
              // );
            }
          );
        this.subscriptions.push(sub);
      }
    } catch (error) {
      this.notificationService.showErrorTranslated(
        'users.moduleSelection.invalidModuleUrl',
        ''
      );
    }
  }

  /**
   * Determine if re-authentication is needed
   * Re-auth is needed if:
   * 1. User doesn't have a role in their current token
   * 2. User is selecting a different role than currently assigned
   * 3. User is selecting a different module than currently assigned
   */
  private needsReAuthentication(
    currentUser: any,
    module: UserModule,
    storedCredentials: string | null
  ): boolean {
    if (!currentUser) {
      return true; // No user data - need to authenticate
    }

    // If no stored credentials, we can't re-authenticate anyway
    if (!storedCredentials) {
      return false; // Skip re-auth, use existing token
    }

    const selectedRoleId = module.selectedRole?.id;

    // Check if user already has this module and role combination
    const hasMatchingModule = currentUser.moduleId === module.id;
    const hasMatchingRole = selectedRoleId
      ? currentUser.roleId === selectedRoleId
      : true;

    // Re-auth needed if module or role is different, or if no role assigned yet
    return !hasMatchingModule || !hasMatchingRole || !currentUser.roleId;
  }

  /**
   * Check if the returnUrl belongs to the selected module
   * Returns true if returnUrl matches the module's routes, false otherwise
   */
  private shouldUseReturnUrl(
    moduleId: number,
    returnUrl: string | null
  ): boolean {
    if (!returnUrl) {
      return false;
    }

    // Mystery Shopper Module (moduleId = 2)
    if (moduleId === 2) {
      // Mystery Shopper URLs
      const mysteryShopperPaths = [
        '/msm-checklist/',
        '/general-dashboard/',
        '/project-head-ui/',
        '/system-code/',
        '/user-management/',
      ];
      return mysteryShopperPaths.some(path => returnUrl.startsWith(path));
    }
    // Card Tracker Module (moduleId = 1) or other modules
    else {
      // Card Tracker URLs
      const cardTrackerPaths = [
        '/project/',
        '/section/',
        '/tasks/',
        '/milestone/',
        '/project-cr/',
        '/tags/',
        '/user-role/',
        '/roles/',
        '/section-chart/',
        '/reports/',
        '/project-head/',
        '/dashboard',
        '/system-code/',
        '/user-management/',
      ];
      return cardTrackerPaths.some(path => returnUrl.startsWith(path));
    }
  }

  /**
   * Logout
   */
  logout(): void {
    // Clear stored credentials
    sessionStorage.removeItem('tempLoginCredentials');
    this.currentUserService.logOut();
  }

  /**
   * Toggle dropdown for role selection
   */
  toggleDropdown(module: UserModule): void {
    // Close all other dropdowns first
    this.modules.forEach(m => {
      if (m.id !== module.id) {
        m.dropdownOpen = false;
      }
    });

    // Toggle current dropdown
    module.dropdownOpen = !module.dropdownOpen;
  }

  /**
   * Select a role from dropdown
   */
  selectRole(module: UserModule, role: UserRole): void {
    module.selectedRole = role;
    module.dropdownOpen = false;
  }

  /**
   * Close dropdowns when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Check if click is outside all dropdowns
    if (!target.closest('.custom-dropdown')) {
      this.modules.forEach(m => (m.dropdownOpen = false));
    }
  }
}
